import { useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import HandymanIcon from '@mui/icons-material/Handyman';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { CarContributor, ContributionStatus, SingleResponse } from '../api/types';

interface ContributeButtonProps {
  carId: number;
  initialStatus: ContributionStatus | null;
  onStatusChange?: (status: ContributionStatus) => void;
}

export function ContributeButton({ carId, initialStatus, onStatusChange }: ContributeButtonProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ContributionStatus | null>(initialStatus);
  const [loading, setLoading] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<SingleResponse<CarContributor>>(
        `/api/v1/cars/${carId}/contributors`,
        { method: 'POST' }
      );
      setStatus(res.data.status);
      onStatusChange?.(res.data.status);
    } finally {
      setLoading(false);
    }
  };

  if (status === 'approved') {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        color="success"
        variant="outlined"
        label={t('contribute.statusApproved')}
      />
    );
  }

  if (status === 'pending') {
    return (
      <Chip
        icon={<HourglassBottomIcon />}
        color="warning"
        variant="outlined"
        label={t('contribute.statusPending')}
      />
    );
  }

  if (status === 'denied') {
    return (
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<BlockIcon />}
        onClick={handleRequest}
        disabled={loading}
      >
        {t('contribute.statusDeniedRetry')}
      </Button>
    );
  }

  return (
    <Button
      variant="outlined"
      startIcon={<HandymanIcon />}
      onClick={handleRequest}
      disabled={loading}
    >
      {loading ? t('contribute.requesting') : t('contribute.request')}
    </Button>
  );
}
